import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import { FormField } from "../customer-form-field";
import { CustomerApiTags } from "./tags";
import { Customer, NewCustomer, TableCustomer } from "./types";
import { getCustomerIdTag, getCustomerListTag } from "./utils";

export const addCustomerApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [CustomerApiTags.customer, CustomerApiTags.tableCustomer],
  });
  const customerApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
      customer: build.query<
        { data: Customer },
        { entityId: string; customerId: string; isGuest?: boolean }
      >({
        query: ({ entityId, customerId, isGuest = false }) => ({
          url: isGuest
            ? `/api/entity/${entityId}/guest/customer/${customerId}`
            : `/api/auth/entity/${entityId}/customers/${customerId}`,
          silentError: true,
        }),
        providesTags: (_result, _error, { customerId }) => [
          getCustomerIdTag(customerId),
        ],
      }),
      customersList: build.query<
        PaginatedResponse<Customer>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            search_term?: string;
            sort_by?: string;
            sort_order?: "asc" | "desc";
            search_by?: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/entity/${entityId}/customers`,
          params,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const response = await queryFulfilled;

            response.data.data.forEach((customer) => {
              dispatch(
                customerApi.util.upsertQueryData(
                  "customer",
                  { entityId: args.entityId, customerId: customer.id },
                  { data: customer }
                )
              );
            });
          } catch {
            // Do nothing
          }
        },
        providesTags: (_result, _error, { entityId }) => [
          getCustomerListTag(entityId),
        ],
      }),
      tableCustomersList: build.query<
        PaginatedResponse<TableCustomer>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            sort_by?: {
              id: string;
              type: FormField["type"];
            };
            sort_order?: "asc" | "desc";
            search_term?: string;
            search_by?: { id: string; values: FormField["values"] }[];
            view?: "table";
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/entity/${entityId}/customers/list`,
          method: "POST",
          data: {
            ...params,
            search_term: params.search_term || undefined,
            search_by: params.search_term ? params.search_by : undefined,
          },
        }),
        providesTags: (_result, _error, { entityId }) => [
          getCustomerListTag(entityId),
        ],
      }),
      addCustomer: build.mutation<
        { data: Customer },
        {
          entityId: string;
          fields: NewCustomer;
          logoFile?: File;
        }
      >({
        query: ({ entityId, fields, logoFile }) => {
          const formData = new FormData();
          formData.set("fields", JSON.stringify(fields));
          if (logoFile) formData.set("logo_file", logoFile);

          return {
            url: `/api/auth/entity/${entityId}/customers`,
            data: formData,
            method: "POST",
            headers: { "Content-Type": "multipart/form-data" },
          };
        },
      }),
      updateCustomer: build.mutation<
        { data: Customer },
        {
          entityId: string;
          fields: NewCustomer;
          logoFile?: File;
          customerId: string;
        }
      >({
        query: ({ entityId, customerId, fields, logoFile }) => {
          const binData = new FormData();
          binData.append("fields", JSON.stringify(fields));
          if (logoFile) binData.set("logo_file", logoFile);

          return {
            url: `/api/auth/entity/${entityId}/customers/${customerId}`,
            data: binData,
            method: "PATCH",
          };
        },
        invalidatesTags: (_result, _error, { customerId }) => [
          getCustomerIdTag(customerId),
        ],
      }),
      deleteCustomer: build.mutation<
        void,
        {
          entityId: string;
          customerId: string;
        }
      >({
        query: ({ entityId, customerId }) => ({
          url: `/api/auth/entity/${entityId}/customers/${customerId}`,
          method: "DELETE",
        }),
      }),
    }),
  });

  return customerApi;
};

export type CustomerApi = ReturnType<typeof addCustomerApi>;
